import React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  Search,
  Clock,
  Wifi,
  ShieldAlert,
  Database,
  Code,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ProfileError as ProfileErrorType } from "@/hooks/profile/useProfileError";

interface ProfileErrorProps {
  error?: ProfileErrorType | null;
  onRetry?: () => void;
}

const ProfileError = ({ error, onRetry }: ProfileErrorProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isSimplifiedView = searchParams.get("timeout") === "true";
  const isRetryable =
    error?.type &&
    [
      "network_error",
      "server_error",
      "rate_limited",
      "timeout_error",
      "function_missing_error",
      "unknown",
    ].includes(error.type);

  // Get error messages based on error type
  const getErrorTitle = () => {
    if (!error) return "Profile Not Available";

    switch (error.type) {
      case "not_found":
        return "Profile Not Found";
      case "permission_denied":
        return "Access Denied";
      case "network_error":
        return "Connection Issue";
      case "rate_limited":
        return "Too Many Requests";
      case "server_error":
        return "Server Error";
      case "timeout_error":
        return isSimplifiedView
          ? "Simplified View (Timeout)"
          : "Database Timeout";
      case "function_missing_error":
        return "Database Function Error";
      default:
        return "Profile Not Available";
    }
  };

  const getErrorDescription = () => {
    if (!error)
      return "We couldn't load this profile. It may have been removed, is private, or you don't have permission to view it.";

    switch (error.type) {
      case "not_found":
        return "The requested profile does not exist or has been removed.";
      case "permission_denied":
        return "You don't have permission to view this profile. It may be private or restricted.";
      case "network_error":
        return `Unable to connect to the server. ${
          navigator.onLine
            ? "The server might be temporarily unavailable."
            : "You appear to be offline."
        } Please check your internet connection and try again.`;
      case "rate_limited":
        return "You've made too many requests. Please wait a moment before trying again.";
      case "server_error":
        return "The server encountered an error while processing your request. Please try again later.";
      case "timeout_error":
        return isSimplifiedView
          ? "This profile is in simplified view mode due to its large size. Some portfolio items may not be displayed."
          : "This profile has a lot of data which caused the database query to timeout. Try again or view a simplified version.";
      case "function_missing_error":
        return "A required database function is missing. This might be a temporary database issue. Please try again or contact support if the issue persists.";
      default:
        return "We couldn't load this profile due to an unexpected error.";
    }
  };

  const getErrorIcon = () => {
    if (!error)
      return <AlertCircle className="h-10 w-10 text-muted-foreground" />;

    switch (error.type) {
      case "network_error":
        return <Wifi className="h-10 w-10 text-amber-500" />;
      case "timeout_error":
        return <Clock className="h-10 w-10 text-amber-500" />;
      case "function_missing_error":
        return <Code className="h-10 w-10 text-amber-500" />;
      case "server_error":
        return <Database className="h-10 w-10 text-red-500" />;
      case "permission_denied":
        return <ShieldAlert className="h-10 w-10 text-red-500" />;
      default:
        return <AlertCircle className="h-10 w-10 text-muted-foreground" />;
    }
  };

  // If the error is a timeout, provide an option to view a simplified version
  const getSimplifiedViewButton = () => {
    if (error?.type === "timeout_error" && !isSimplifiedView) {
      // Append timeout=true to the current URL
      const currentPath = location.pathname;
      const simplifiedLink = `${currentPath}?timeout=true`;

      return (
        <Link to={simplifiedLink}>
          <Button
            variant="outline"
            size="lg"
            className="w-full mt-2 flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            View Simplified Profile
          </Button>
        </Link>
      );
    }

    // If already in simplified view, offer to retry normal view
    if (isSimplifiedView) {
      // Remove timeout parameter
      const currentPath = location.pathname;

      return (
        <Link to={currentPath}>
          <Button
            variant="outline"
            size="lg"
            className="w-full mt-2 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Full Profile View
          </Button>
        </Link>
      );
    }

    return null;
  };

  const handleNetworkCheck = () => {
    // Check online status
    if (!navigator.onLine) {
      window.location.reload();
    } else if (onRetry) {
      onRetry();
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
      <div className="text-center max-w-md">
        <Avatar className="h-16 w-16 mx-auto mb-4">
          <AvatarImage
            src="/lovable-uploads/default-profile-avatar.png"
            alt="Default Profile"
          />
          <AvatarFallback>{getErrorIcon()}</AvatarFallback>
        </Avatar>

        <h2 className="text-2xl font-bold mb-2">{getErrorTitle()}</h2>

        <Alert
          variant={
            error?.type === "network_error"
              ? "warning"
              : isSimplifiedView
              ? "warning"
              : "destructive"
          }
          className="mb-6"
        >
          {getErrorIcon()}
          <div>
            <AlertTitle className="ml-2">
              {error?.message || "Error"}
            </AlertTitle>
            <AlertDescription>{getErrorDescription()}</AlertDescription>
          </div>
        </Alert>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {error?.type === "network_error" ? (
            <Button
              variant="default"
              size="lg"
              className="w-full flex items-center gap-2"
              onClick={handleNetworkCheck}
            >
              <RefreshCw className="h-4 w-4" />
              {!navigator.onLine ? "Reload When Online" : "Retry Connection"}
            </Button>
          ) : (
            isRetryable &&
            onRetry &&
            !isSimplifiedView && (
              <Button
                variant="default"
                size="lg"
                className="w-full flex items-center gap-2"
                onClick={onRetry}
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            )
          )}

          {getSimplifiedViewButton()}

          <Link to="/">
            <Button
              variant={isRetryable && onRetry ? "outline" : "default"}
              size="lg"
              className="w-full mt-2 sm:mt-0 flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Homepage
            </Button>
          </Link>

          {user && (
            <Link to="/dashboard">
              <Button
                variant="outline"
                size="lg"
                className="w-full mt-2 sm:mt-0"
              >
                Go to Dashboard
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileError;
